import React, { Suspense, useEffect } from 'react';
import { useQuery } from "react-query";
import * as apiClient from "../api-client";
// Import the CSS
import '../index.css';
import Loader from '../components/LoaderComponent';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import FAQBeach from '../components/HolidayFAQs/BeachFaq';
import BlogSliderType from '../components/Blogs/BlogSliderType';
import HolidayTypeHotelsList from '../components/HolidayTypeHotelList';
import Slider1 from "../components/CountrySliders/Maldives/BeachSlider";
import Slider2 from "../components/CountrySliders/Mauritius/BeachSlider";
import HotelSliderSkeleton from '../components/Skeletons/HotelSliderSkeleton';
import ReadMoreLines from '../components/ReadMoreLines';

// Lazy load the components
// const LazyLatestDestinationCard = React.lazy(() => import('../components/LastestDestinationCard'));
const LazyWhyChoose = React.lazy(() => import('../components/WhyChoose/Beach'));
const LazyVacationTypes = React.lazy(() => import('../components/Home/VacationTypes'));

const PageBeach = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: hotels } = useQuery("fetchAllInclusiveHotels", apiClient.fetchHotels);

  // const [isExpanded, setIsExpanded] = useState(false);
  // const toggleText = () => setIsExpanded(!isExpanded);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Beach Holidays & Tropical Escapes | Halo Holidays</title>
          <meta name="description" content="Escape to paradise with Halo Holidays' handpicked beach holidays. Discover stunning coastlines, luxury resorts, and all-inclusive packages for your perfect sun-soaked getaway." />
          {/* <meta name="keywords" content="travel, hotels, holidays, deals, holiday packages" /> */}
        </Helmet>
      </HelmetProvider>

      <section className="max-h-640 md:min-h-0 lg:min-h-0 my-8">
        <hr className="mb-10" />
        <h2 className="text-2xl font-semibold items-center justify-center flex mt-10">More reasons to choose us</h2>
        <Suspense fallback={<Loader />}>
          <LazyWhyChoose />
        </Suspense>
        <hr className="mb-10 mt-10" />
      </section>

      <section className="mx-4 my-4 md:mx-0 md:my-0">

        <div className='mt-2 mb-4'>
          <h1 className="text-2xl font-semibold">Beach Holidays</h1>
          <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
            <p className="text-black md:mb-1 md:mt-2">
              Dreaming of sun-kissed shores, crystal-clear waters, and the gentle lull of the waves? Beach holidays are the perfect antidote to the stresses of everyday life, offering an idyllic escape for couples seeking romance, families craving fun, and solo adventurers in need of a recharge. Halo Holidays invites you to embark on a journey to some of the world's most breathtaking coastal destinations, where relaxation, adventure, and unforgettable moments await.
              <br /><br />
              Imagine strolling hand-in-hand with your loved one along pristine beaches, feeling the soft sand between your toes as you watch the sunset paint the sky with vibrant hues. Picture yourselves indulging in a couples' massage in a beachfront cabana, sipping on exotic cocktails as the gentle waves serenade you, or savoring a candlelit dinner with your toes in the sand. Our curated selection of <a href='https://www.haloholidays.co.uk/couple-holidays' className='text-halo-pink'>couple holidays</a> offers the perfect romantic escape to rekindle the spark and create lasting memories.
              <br /><br />
              But beach holidays aren't just for couples. If you're traveling with family, imagine your children building sandcastles and splashing in the crystal-clear waters, their laughter echoing across the shore. Our family-friendly beach resorts offer a range of activities for all ages, from kids' clubs and water parks to snorkeling and kayaking excursions.
              <br /><br />
              And for those seeking a solo retreat, our beach destinations offer a tranquil escape where you can relax, recharge, and reconnect with yourself. Imagine finding solace in a hidden cove, reading a good book, or simply basking in the sun as the soothing sounds of the ocean wash away your worries.
              <br /><br />
              From the overwater bungalows of the Maldives to the vibrant beach clubs of Bali, the sun-drenched shores of Spain and Greece, and the secluded coves of Thailand and the Caribbean, our destinations offer endless possibilities for fun in the sun, sand, and sea. Whether you're seeking a luxurious couples' retreat, an action-packed family holiday, or a rejuvenating solo escape, Halo Holidays has the perfect beach getaway to fulfill your every desire. So pack your swimsuit, grab your sunscreen, and let us transport you to a beach paradise that will leave you feeling refreshed, revitalized, and utterly blissed out.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Discover Our Handpicked Beach Front Resorts</h2>
            <p className='text-black'>
              Immerse yourself in pure luxury at our handpicked collection of beach holiday resorts. These havens of relaxation boast breathtaking locations, stunning ocean views, and world-class amenities that cater to your every whim. Whether you're seeking an <a href='https://www.haloholidays.co.uk/all-inclusive-holidays' className='text-halo-pink'>all-inclusive escape</a> with unlimited food and drinks, or a boutique hotel with personalized service, our beach resorts offer the perfect blend of comfort, style, and convenience. Imagine yourself lounging by infinity pools, indulging in rejuvenating spa treatments, and savouring gourmet cuisine as you soak up the sun and unwind.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Not a Beach Bum? No Worries, We Have Other Amazing Escapes</h2>
            <p className='text-black'>
              Looking for a different kind of adventure? Halo Holidays offers a diverse range of holiday options beyond beach getaways. Discover our collection of luxury holidays for a truly indulgent experience, or embark on a family holiday filled with fun and adventure for all ages. If you're seeking a cultural immersion, we offer exciting city breaks and historical tours to destinations around the world. And for those who love the thrill of spontaneity, our <a href='https://www.haloholidays.co.uk/last-minute-holidays' className='text-halo-pink'>last-minute holidays</a> can whisk you away to paradise at a moment's notice. No matter what your travel style or preferences, Halo Holidays is here to curate your dream holiday. Contact our expert travel advisors today to start planning your next unforgettable adventure!
            </p>
          </ReadMoreLines>
        </div>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='mt-6 -mb-2 md:mt-0 md:mb-2'>
              <h2 className="text-xl font-semibold">Maldives Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Beach" holidayCountry="Maldives" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider1 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='md:mt-4 -mb-2 md:mb-2'>
              <h2 className="text-xl font-semibold">Mauritius Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Beach" holidayCountry="Mauritius" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider2 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <FAQBeach />

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">Latest All Inclusive Destinations</h2>
      <p>Most recent all-inclusive destinations added by our hosts</p>
      </Suspense> */}
        {/* Displaying the top row hotels */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div>
       */}
        {/* Displaying the bottom row hotels */}
        {/* <div className="grid md:grid-cols-3 gap-4">
        {bottomRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}

        <Suspense fallback={<Loader />}>
          <div className='p-4 md:p-0 md:mt-12 md:mb-12'>
            <LazyVacationTypes />
          </div>
        </Suspense>

        <section className='p-4 md:p-0'>
          <h2 className='text-2xl font-semibold mb-4'>Related Blogs</h2>
          <BlogSliderType blogType='beach_vibes' />
        </section>

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">More All Inclusive Destinations</h2>
      <p>Explore more all-inclusive destinations</p>
      </Suspense> */}

        {/* Optionally, display more hotels with AllInclusiveHotelCard */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyAllInclusiveHotelCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}
      </section>
    </>
  );
};

export default PageBeach;
